'use client';

import { useProjectSettings } from 'frontastic';
import { useCallback } from 'react';

const TICKS_BETWEEN_EPOCHS = 621355968000000000n;

const ticksToDate = (ticks: string | number | bigint | null | undefined) => {
  if (!ticks) {
    return null;
  }

  try {
    const ticksBigInt = BigInt(ticks);
    const unixMilliseconds = (ticksBigInt - TICKS_BETWEEN_EPOCHS) / 10000n;

    return new Date(Number(unixMilliseconds));
  } catch {
    return null;
  }
};

const dateToTicks = (date: Date) => {
  return (BigInt(date.getTime()) * 10000n + TICKS_BETWEEN_EPOCHS).toString();
};

const hasExpired = (issuedAt: Date, ttlHours = 8) => {
  const expiresAt = new Date(issuedAt.getTime() + ttlHours * 60 * 60 * 1000);
  return Date.now() >= expiresAt.getTime();
};

const useUserImageUrl = () => {
  const { projectSettings } = useProjectSettings();

  // For images that come from the mock site, they have a "version" query param that describes when the url was
  // generated. However, the urls do not actually expire, so we don't see broken images in the site when the url
  // is past the ttl. So here we manually check if they are expired, and replace them with a broken image url, so
  // it is easier to test the image refresh flow, since we will actually see broken images when they are expired.
  const getUserImageUrl = useCallback(
    (url: string) => {
      if (!url.startsWith('https://media2.ffvoyager.dev/api/v1/upload')) {
        return url;
      }

      try {
        const parsedUrl = new URL(url);
        const version = parsedUrl.searchParams.get('version');

        if (!version) {
          return url;
        }

        const issuedAt = ticksToDate(version);

        if (!issuedAt) {
          return url;
        }

        const googlePhotosTtlHours = projectSettings?.GOOGLE_PHOTOS_TTL_HOURS ?? 8;
        if (hasExpired(issuedAt, googlePhotosTtlHours)) {
          return 'bad-url';
        }

        return url;
      } catch {
        return url;
      }
    },
    [projectSettings],
  );

  return {
    getUserImageUrl,
  };
};

export default useUserImageUrl;